import { AfterViewInit, Directive, ElementRef, viewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

import { ICellEditorAngularComp } from 'ag-grid-angular';

import { IBarCellEditorParams } from './bar-cell-editor.params-contract';
import { BarKeyCode } from './bar-key-code.enum';

/** Base class for input-based editor components. */
@Directive()
export abstract class BarBaseInputCellEditor<TParams extends IBarCellEditorParams> implements ICellEditorAngularComp, AfterViewInit {
    readonly input = viewChild<ElementRef<HTMLInputElement>>('input');

    protected params: TParams;
    protected formControl: FormControl;

    agInit(params: TParams): void {
        this.params = params;
        // here we pass the formControl or create one to make the view work
        this.formControl = params.formControl || new FormControl(params.value);
        this.onInit();
    }

    // dont use afterGuiAttached for post gui events - hook into ngAfterViewInit instead for this
    ngAfterViewInit(): void {
        setTimeout(() => {
            const focusedCell = this.params.api.getFocusedCell();
            if (focusedCell != null && focusedCell.column == this.params.column) {
                this.input().nativeElement.focus();

                if (this.isAllowedCharacter(this.params.eventKey)) {
                    this.formControl.setValue(this.params.eventKey);
                    const input = this.input().nativeElement;
                    // also adapt value (eg. datepicker, a random char is not a valid date, formControl is bound to Date)
                    input.value = this.params.eventKey;
                    input.setSelectionRange(input.value.length, input.value.length);
                    input.focus();
                }
            }
        });
    }

    getValue() {
        let value = this.formControl.value;

        if (this.params.onGetValue) {
            value = this.params.onGetValue(value);
            this.formControl.setValue(value);
        }
        return value;
    }

    isPopup(): boolean {
        return false;
    }
    onKeyDown(event: KeyboardEvent): void {
        const key = event.which || event.keyCode;

        switch (key) {
            case BarKeyCode.Enter: {
                event.preventDefault();
                event.stopPropagation();

                if (this.params.api.getGridOption('enterNavigatesVerticallyAfterEdit')) {
                    // naar volgende cell gaan indien zo geconfigureerd
                    this.params.api.setFocusedCell(this.params.rowIndex + 1, this.params.column.getColId());
                } else {
                    // NOTE here we use a timeout because if we stopEditing while having stopEditingWhenGridLosesFocus: true then it would give an error and fail completely (used with editType: 'fullRow')
                    window.setTimeout(() => {
                        this.params.api.stopEditing(false);
                    });
                    // anders default gedrag
                    this.params.api.setFocusedCell(this.params.rowIndex, this.params.column.getColId());
                }
                break;
            }
        }
    }

    focusIn() {
        this.input().nativeElement.focus();
        this.input().nativeElement.select();
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected onInit(): void {}

    protected isAllowedCharacter(eventKey: string): boolean {
        if (eventKey === undefined || eventKey === null) return false;
        // Define forbidden keys
        const forbiddenKeys = [
            'Enter',
            'Tab',
            'Escape',
            'ArrowUp',
            'ArrowDown',
            'ArrowLeft',
            'ArrowRight',
            'Backspace',
            'Delete',
            'Home',
            'End',
            'PageUp',
            'PageDown',
            'Insert',
            'F1',
            'F2',
            'F3',
            'F4',
            'F5',
            'F6',
            'F7',
            'F8',
            'F9',
            'F10',
            'F11',
            'F12'
        ];

        // Check if the key is forbidden
        if (forbiddenKeys.includes(eventKey)) return false;

        // Allow only printable characters
        return eventKey.length === 1 && /^[\w\d\s\p{P}äëïöüÄËÏÖÜáéíóúÁÉÍÓÚñÑ]$/u.test(eventKey);
    }
}
